import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { wormColors } from "core/src/core/constants.js";
import type { ChatMessage } from "../../online/net";
import type { Player } from "../../shared/types";
import { flagUrlSquare, isFlagCode } from "../../shared/flags";
import { WORM_COLOR_IDS } from "../../shared/worm-colors";
import KeyPicker, { type KeyPickerHandle } from "../../shared/key-picker/KeyPicker";
import { type OnlineSession } from "../play/game/RemoteGame";
import ColorSwatchPicker from "./ColorSwatchPicker";
import { B, Check, Crown, Kick, Link, Lock, Globe, Plus, Qr, useLand } from "./RoomsUI";
import s from "./rooms.module.scss";

// The pieces of a live room (/lobby/CODE), shared by the host's and a guest's
// view: the seats, the invite card and the chat. They read the OnlineSession the
// page's RemoteGame reports; the page owns the session and the rules.

const colorOf = (id: string) => (wormColors as any)[id] || "#888";
// At most this many rows before the rest of the empty seats fold into a line.
const MAX_ROWS = 12;

// A seat of yours: the colour, the name and (with a keyboard) the keys can be
// changed right in the row.
export interface SeatEdit {
  setColor: (id: string, colorId: string) => void;
  setName: (id: string, name: string) => void;
  setKey: (id: string, side: "left" | "right", key: string) => void;
  removePlayer: (id: string) => void;
}

function Keys({ p, edit }: { p: Player; edit: SeatEdit }) {
  // Picking the left key arms the right picker at once, so a fast second press lands on it.
  const rightRef = useRef<KeyPickerHandle>(null);
  return (
    <span className={s.keys}>
      <KeyPicker currentKey={p.left ?? undefined} onKeyPicked={(k) => { edit.setKey(p.id, "left", k); rightRef.current?.select(); }} />
      <KeyPicker ref={rightRef} currentKey={p.right ?? undefined} onKeyPicked={(k) => edit.setKey(p.id, "right", k)} />
    </span>
  );
}

// One of your own seats, editable. Also the local setup's rows.
export function OwnSeat({ p, edit, flag, keys, removable, tag, wide }: { p: Player; edit: SeatEdit; flag?: string | null; keys?: boolean; removable?: boolean; tag?: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`${s.seat} ${s.seatOwn} ${wide ? s.seatWide : ""}`}>
      <span className={s.swatchPick}><ColorSwatchPicker colorId={p.colorId} colors={WORM_COLOR_IDS} onChange={(c) => edit.setColor(p.id, c)} /></span>
      {flag && isFlagCode(flag) && <img src={flagUrlSquare(flag)} alt="" />}
      <input className={s.nameIn} value={p.name} onChange={(e) => edit.setName(p.id, e.target.value)} aria-label="Player name" spellCheck={false} />
      {tag}
      {keys && <Keys p={p} edit={edit} />}
      {removable && <B className={s.kick} onClick={() => edit.removePlayer(p.id)} aria-label={`Remove ${p.name}`} title="Remove"><Kick /></B>}
    </div>
  );
}

// One seat a line (the room's players column, the local game): every row the
// same columns, so they line up down the list: the colour, the flag, the name
// , a bot's level or a player's keys, and the cross
// that removes it. No second line of text.
const levelName = (skill?: number) => (skill == null ? "Medium" : skill <= 3 ? "Easy" : skill <= 7 ? "Medium" : "Hard");
const LEVEL_C: Record<string, string> = { Easy: "#7CFC66", Medium: "#f5c542", Hard: "#ff5c5c" };
// A bot's level as the old setup showed it: a small tag in the level's colour.
export function LevelTag({ level, dim }: { level: string; dim?: boolean }) {
  return <span className={`${s.levelTag} ${dim ? s.levelDim : ""}`} style={{ "--lc": LEVEL_C[level] || "#8ea3bd" } as React.CSSProperties}>{level}</span>;
}
export const botTag = (skill?: number) => <LevelTag level={levelName(skill)} />;
export function SeatLine({ color, flag, name, keys, action, own, empty, noFlag }: {
  color: React.ReactNode; flag?: string | null; name: React.ReactNode; keys?: React.ReactNode; action?: React.ReactNode; own?: boolean; empty?: boolean;
  noFlag?: boolean; // a list without flags (the local game): no empty column for them
}) {
  return (
    <div className={`${s.line} ${empty ? s.lineEmpty : ""} ${noFlag ? s.lineNoFlag : ""}`}>
      <span className={s.lineColor}>{color}</span>
      {!noFlag && <span className={s.lineFlag}>{flag && isFlagCode(flag) ? <img src={flagUrlSquare(flag)} alt="" /> : null}</span>}
      <span className={s.lineText}>{name}</span>
      <span className={s.lineKeys}>{keys}</span>
      <span className={s.lineAct}>{action}</span>
    </div>
  );
}
export const Dot = ({ colorId }: { colorId?: string }) => <i className={s.lineDot} style={colorId ? { background: colorOf(colorId) } : undefined} />;
export function OwnLine({ p, edit, flag, keys, removable, tag, noFlag }: { p: Player; edit: SeatEdit; flag?: string | null; keys?: boolean; removable?: boolean; tag?: React.ReactNode; noFlag?: boolean }) {
  return (
    <SeatLine own noFlag={noFlag}
      color={<span className={s.swatchPick}><ColorSwatchPicker colorId={p.colorId} colors={WORM_COLOR_IDS} onChange={(c) => edit.setColor(p.id, c)} /></span>}
      flag={flag}
      name={<input className={s.nameIn} value={p.name} onChange={(e) => edit.setName(p.id, e.target.value)} aria-label="Player name" spellCheck={false} />}
      keys={keys ? <Keys p={p} edit={edit} /> : tag}
      action={removable ? <B className={s.kick} onClick={() => edit.removePlayer(p.id)} aria-label={`Remove ${p.name}`} title="Remove"><Kick /></B> : null}
    />
  );
}

// The players in the room. Your own seats (one per player on this device) can
// be edited in place; the host can remove anyone else. Empty seats wait.
export function Seats({ session, mine, edit, cap, hostId, keys, foot, single, emptyLabel, bare, noHead }: {
  session: OnlineSession | null; mine: Player[]; edit: SeatEdit; cap: number; hostId: string | null; keys: boolean; foot?: React.ReactNode;
  single?: boolean;               // one seat a line (the room's players column)
  emptyLabel?: React.ReactNode;   // what an empty seat says ("Waiting for player")
  bare?: boolean;                 // no box of its own (it sits in the room's Panel)
  noHead?: boolean;               // no "Players  2 of 8" line (a phone: all the room for the rows)
}) {
  const players: any[] = session?.players ?? [];
  const youIds = session?.youIds ?? [];
  const isHost = !!session?.isHost;
  const empties = Math.max(0, cap - players.length);
  // (one seat a line, the column scrolls: every seat is shown, nothing folds away)
  const shownEmpties = single ? empties : Math.max(0, Math.min(empties, MAX_ROWS - players.length));
  return (
    <div className={bare ? s.seatsBare : s.seatsBox}>
      {!noHead && (
        <div className={s.labelRow}>
          <div className={s.label}>Players</div>
          <span>{players.length} of {cap}</span>
        </div>
      )}
      <div className={`${s.seats} ${single ? s.seatsOne : ""}`}>
        {single && players.map((p) => {
          const k = youIds.indexOf(p.id);
          const own = k >= 0 ? mine[k] : null;
          const host = p.id === hostId;
          if (own) return <OwnLine key={p.id} p={own} edit={edit} flag={p.flag} keys={keys} removable={k > 0} />;
          return (
            <SeatLine key={p.id} color={<Dot colorId={p.colorId} />} flag={p.flag} name={<b>{p.name}</b>} keys={p.type === "bot" ? botTag(p.skill) : null}
              action={isHost && k < 0 ? <B className={s.kick} onClick={() => (p.type === "bot" ? session?.kickBot(p.id) : session?.kickPlayer(p.id))} aria-label={`Remove ${p.name}`} title="Remove from room"><Kick /></B> : null} />
          );
        })}
        {single && Array.from({ length: shownEmpties }).map((_, i) => (
          <SeatLine key={"e" + i} empty color={<Dot />} name={<b>Open seat</b>} keys={emptyLabel} />
        ))}
        {!single && players.map((p) => {
          const k = youIds.indexOf(p.id);
          const own = k >= 0 ? mine[k] : null;
          const host = p.id === hostId;
          // Someone else's seat is half a row: the crown alone marks the host there.
          const tag = host ? <span className={s.host} title="Host"><Crown />{own && !single ? "Host" : <span className={s.srOnly}>Host</span>}</span> : k >= 0 ? <span className={s.youTag}>You</span> : null;
          if (own) return <OwnSeat key={p.id} p={own} edit={edit} flag={p.flag} keys={keys} wide={keys} removable={k > 0} tag={tag} />;
          return (
            <div key={p.id} className={s.seat}>
              <i className={s.worm} style={{ background: colorOf(p.colorId), color: colorOf(p.colorId) }} />
              {p.flag && isFlagCode(p.flag) && <img src={flagUrlSquare(p.flag)} alt="" />}
              <b>{p.name}</b>
              {tag}
              {isHost && k < 0 && (
                <B className={s.kick} onClick={() => (p.type === "bot" ? session?.kickBot(p.id) : session?.kickPlayer(p.id))} aria-label={`Remove ${p.name}`} title="Remove from room"><Kick /></B>
              )}
            </div>
          );
        })}
        {!single && Array.from({ length: shownEmpties }).map((_, i) => (
          <div key={"e" + i} className={`${s.seat} ${s.seatEmpty}`}><i className={s.worm} />{emptyLabel ?? "Waiting for player"}</div>
        ))}
      </div>
      {/* (only when it says something: an empty foot took the list's last rows) */}
      {(foot || empties > shownEmpties) && (
        <div className={s.seatsFoot}>
          {empties > shownEmpties && <span>and {empties - shownEmpties} more seats</span>}
          {foot}
        </div>
      )}
    </div>
  );
}

// The invite card: the code, big, with Copy link. The host also sees (and can
// switch) whether the room is listed under open rooms.
export function Invite({ code, pub, onTogglePublic }: { code: string; pub?: boolean; onTogglePublic?: () => void }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://achtungroyale.io";
  const link = `${origin}/lobby/${code}`;
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const land = useLand();
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = () => {
    try { navigator.clipboard?.writeText(link); } catch { /* clipboard blocked */ }
    setCopied(true);
  };
  return (
    <div className={s.invite}>
      <div className={s.inviteTop}>
        <span>
          <small>Invite friends</small>
          <b className={s.code}>{code}</b>
        </span>
        <B className={s.qrBtn} onClick={() => setQr(true)} aria-label="Show a QR code" title="Show a QR code"><Qr /></B>
        <B className={`${s.copy} ${copied ? s.copied : ""}`} onClick={copy}>{copied ? <Check /> : <Link />}{copied ? "Copied" : "Copy link"}</B>
      </div>
      <div className={s.url}>
        {onTogglePublic ? (
          <B className={s.pubBtn} onClick={onTogglePublic} title={pub ? "Make private" : "Make public"}>
            {pub ? <Globe /> : <Lock />}{pub ? "Public" : "Private"}
          </B>
        ) : null}
        <span>{link.replace(/^https?:\/\//, "")}</span>
      </div>
      {qr && createPortal(
        <div className={`${s.qrScrim} ${land ? s.qrLand : ""}`} onClick={() => setQr(false)}>
          <div className={s.qrCard} onClick={(e) => e.stopPropagation()}>
            <div className={s.qrCode}><QRCodeSVG value={link} size={190} bgColor="#ffffff" fgColor="#0b0f1a" level="M" /></div>
            <b>{code}</b>
            <span>Scan to join this room</span>
            <B className={s.change} onClick={() => setQr(false)}>Close</B>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// The room's chat: the log and the input.
export function ChatBox({ chat, onSend, bare }: { chat?: ChatMessage[]; onSend?: (text: string) => void; bare?: boolean }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat?.length]);
  const send = () => {
    const t = draft.trim();
    if (!t || !onSend) return;
    onSend(t);
    setDraft("");
  };
  return (
    <div className={bare ? s.chatBare : s.chatBox}>
      <div className={s.label}>Chat</div>
      <div className={s.msgs} ref={scrollRef}>
        {!chat || chat.length === 0 ? (
          <p className={s.chatHint}>Say hi to the room.</p>
        ) : chat.map((m, i) => (m.system
          ? <p key={i} className={s.sysMsg}>{m.text}</p>
          : <p key={i}><b style={{ color: (m.colorId && colorOf(m.colorId)) || "#19f0b0" }}>{m.name || "Player"}</b>{m.text}</p>))}
      </div>
      <form className={s.chatIn} onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 300))} placeholder="Say something" aria-label="Chat message" disabled={!onSend} />
        <B type="submit" disabled={!draft.trim()}>Send</B>
      </form>
    </div>
  );
}

export function AddBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return <B className={s.quiet} onClick={onClick} disabled={disabled}><Plus />{children}</B>;
}
